import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, map, Subject, take, takeUntil } from 'rxjs';
import { AcademyLesson } from '../../../academy/academy-catalog';
import { AcademyQuestion, AcademyService, AcademyTourText, LessonStatus } from '../../../academy/academy.service';
import { AcademyQuizResult } from '../../../components/academy/academy-quiz.component';
import { AgentSpotlightService, SpotlightStep } from '../../../agent/agent-spotlight.service';
import { AGENT_CATALOG } from '../../../agent/agent-catalog';
import { AcademyDoneChecks } from '../../../academy/academy-done-checks';

/** Ein Schritt einer Tour im Katalog (`portal-ui-catalog.json` → `tours`). */
interface CatalogTourStep {
  anchor: string;
  page?: string;
  await?: boolean;
  advance?: 'click';
  alt?: string[];
  skip_if_visible?: string;
}

interface SideRow {
  lesson: AcademyLesson;
  title: string;
  status: LessonStatus;
}

/**
 * Eine Lektion (`/academy/lessons/:lessonId`) im Lektionsmuster (PRD
 * academy-content › 1): Warum → Film → Kurz erklärt → Selbst machen
 * (geführter Klickpfad + Prüfung am echten Zustand) → Quiz → Weiter.
 *
 * Fortschritt: Öffnen = begonnen. Abgeschlossen ist sie, sobald jede
 * Quizfrage richtig beantwortet ist ODER die Erledigt-Prüfung besteht —
 * ohne beides mit „Abschließen“, wenn die Lektion kein Quiz hat.
 */
@Component({
  selector: 'app-academy-lesson',
  templateUrl: './academy-lesson.component.html',
  styleUrls: ['./academy-lesson.component.scss'],
})
export class AcademyLessonComponent implements OnInit, OnDestroy {
  lesson: AcademyLesson | null = null;
  title = '';
  summary = '';
  points: string[] = [];
  topicTitle = '';
  questions: AcademyQuestion[] = [];
  status: LessonStatus = 'not_started';
  next: AcademyLesson | null = null;
  nextTitle = '';
  side: SideRow[] = [];
  quizResult: AcademyQuizResult | null = null;
  ready = false;
  why = '';
  captions: string[] = [];
  /** Schritte des Klickpfads (leer = keiner). */
  tourSteps: SpotlightStep[] = [];
  /** Erledigt-Prüfung: unbekannt, läuft, bestanden, offen. */
  doneState: 'none' | 'checking' | 'done' | 'open' = 'none';
  requires: SideRow[] = [];
  private quizFocused = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private academy: AcademyService,
    private route: ActivatedRoute,
    private router: Router,
    private spotlight: AgentSpotlightService,
    private translate: TranslateService,
    private doneChecks: AcademyDoneChecks,
  ) {}

  ngOnInit(): void {
    this.academy.ensureContent();
    this.academy.loadProgress();

    const id$ = this.route.paramMap.pipe(map((p) => p.get('lessonId') ?? ''));
    // Neue Lektion → als begonnen merken (einmal, sobald der Stand geladen ist).
    combineLatest([id$, this.academy.progressLoaded$.pipe(filter(Boolean))])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([id]) => {
        this.quizResult = null;
        if (this.academy.lessonById(id)) this.academy.markStarted(id);
      });

    combineLatest([
      id$,
      this.academy.content$.pipe(filter((c) => !!c)),
      this.academy.progress$,
      this.academy.progressLoaded$.pipe(filter(Boolean)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([id]) => {
        const lesson = this.academy.lessonById(id);
        if (!lesson) {
          this.router.navigate(['/academy']);
          return;
        }
        this.build(lesson);
        this.ready = true;
        this.focusQuizOnce();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onQuizCompleted(result: AcademyQuizResult): void {
    this.quizResult = result;
    if (this.lesson) this.academy.markCompleted(this.lesson.id);
  }

  complete(): void {
    if (this.lesson) this.academy.markCompleted(this.lesson.id);
  }

  /** Ohne Quiz schließt „Weiter“ die Lektion mit ab. */
  goNext(): void {
    if (this.lesson && !this.questions.length) this.academy.markCompleted(this.lesson.id);
    if (this.next) this.router.navigate(['/academy/lessons', this.next.id]);
    else this.router.navigate(['/academy']);
  }

  /** „Klickpfad starten“: zur ersten Seite der Tour, dann führt der
   *  Spotlight Schritt für Schritt — über Seiten hinweg (Schritte mit
   *  `page`), wartend, bis die Person das Element selbst geöffnet hat.
   *  Gespeichert wird nie von der Tour, nur vom Klick der Person. */
  startTour(): void {
    const lesson = this.lesson;
    if (!lesson || !this.tourSteps.length) return;
    const firstPage = this.tourSteps[0].page;
    const route = firstPage ? AGENT_CATALOG.pages[firstPage]?.route : undefined;
    const target = route && !route.includes(':') ? route : lesson.target?.route;
    const steps = this.tourSteps;
    // Am Ende zurück in die Lektion, direkt zum Quiz — die nächste Aktion
    // (Marcel, 29.10.2026). Ohne Quiz zurück zur Lektion.
    const finish = {
      label: this.translate.instant(this.questions.length ? 'academy.lesson.tourToQuiz' : 'academy.lesson.tourToLesson'),
      url: `/academy/lessons/${lesson.id}?focus=quiz`,
    };
    // Erster Schritt ohne Seite (Hauptnavigation, Kopfzeile): der Weg beginnt
    // hier — der Nutzer klickt sich selbst hin, statt hingesprungen zu werden.
    if (!firstPage) {
      this.spotlight.tour(steps, finish);
      return;
    }
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
        // Kein takeUntil(destroy$): die Lektion wird beim Seitenwechsel
        // zerstört, BEVOR NavigationEnd kommt — das Abo endet über take(1).
        take(1),
      )
      .subscribe((e) => {
        if (e instanceof NavigationEnd) this.spotlight.tour(steps, finish);
      });
    this.router.navigate([target ?? '/academy']);
  }

  /** Zurück aus dem Klickpfad (`?focus=quiz`): zum Quiz scrollen, einmal. */
  private focusQuizOnce(): void {
    if (this.quizFocused || this.route.snapshot.queryParamMap.get('focus') !== 'quiz') return;
    this.quizFocused = true;
    setTimeout(() => {
      const el = document.getElementById('lesson-quiz') ?? document.getElementById('lesson-actions');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  }

  /** Prüfung am echten Zustand erneut ausführen. */
  checkDone(): void {
    const lesson = this.lesson;
    if (!lesson?.doneCheck) return;
    this.doneState = 'checking';
    this.doneChecks
      .check(lesson.doneCheck)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((done) => {
        if (this.lesson?.id !== lesson.id) return;
        this.doneState = done ? 'done' : 'open';
        if (done) this.academy.markCompleted(lesson.id);
      });
  }

  /** „Im Portal ausprobieren“: zur Funktion; mit Anker zeigt der Spotlight
   *  das Ziel, sobald die Seite da ist (wie „Gehe zu“ im Portalhinweis). */
  tryInPortal(): void {
    const target = this.lesson?.target;
    if (!target) return;
    if (target.anchor) {
      const step = {
        anchor: target.anchor,
        title: this.title,
        body: this.translate.instant('academy.lesson.trySpotlight'),
      };
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
          // Kein takeUntil(destroy$): die Lektion wird beim Seitenwechsel
          // zerstört, BEVOR NavigationEnd kommt — das Abo endet über take(1).
          take(1),
        )
        .subscribe((e) => {
          if (e instanceof NavigationEnd) this.spotlight.highlight(step);
        });
    }
    this.router.navigate([target.route], { queryParams: target.queryParams });
  }

  private buildTour(lesson: AcademyLesson, texts: (string | AcademyTourText)[]): SpotlightStep[] {
    const tours = AGENT_CATALOG.tours as Record<string, { steps?: CatalogTourStep[] }>;
    const steps = lesson.tour ? (tours[lesson.tour]?.steps ?? []) : [];
    return steps.map((st, i) => {
      const text = texts[i];
      return {
        anchor: st.anchor,
        page: st.page,
        await: st.await,
        advance: st.advance,
        alt: st.alt,
        skipIfVisible: st.skip_if_visible,
        title: `${this.academy.lessonTitle(lesson.id)} · ${i + 1}/${steps.length}`,
        body: typeof text === 'string' ? text : (text?.text ?? ''),
        tip: typeof text === 'string' ? undefined : text?.tip,
      };
    });
  }

  trackSide(_: number, r: SideRow): string {
    return r.lesson.id;
  }

  private build(lesson: AcademyLesson): void {
    const changed = this.lesson?.id !== lesson.id;
    this.lesson = lesson;
    const text = this.academy.lessonText(lesson.id);
    this.why = text?.why ?? '';
    this.captions = text?.scenes ?? [];
    this.tourSteps = this.buildTour(lesson, text?.tour ?? []);
    this.requires = (lesson.requires ?? [])
      .map((id) => this.academy.lessonById(id))
      .filter((l): l is AcademyLesson => !!l)
      .map((l) => ({ lesson: l, title: this.academy.lessonTitle(l.id), status: this.academy.lessonStatus(l.id) }));
    if (changed && lesson.doneCheck) this.checkDone();
    if (changed && !lesson.doneCheck) this.doneState = 'none';
    this.title = text?.title ?? lesson.id;
    this.summary = text?.summary ?? '';
    this.points = text?.points ?? [];
    this.topicTitle = this.academy.topicText(lesson.topic).title;
    // Fragen nur beim Wechsel neu bauen — sonst setzte jeder Fortschritts-
    // Tick das laufende Quiz zurück.
    if (changed || !this.questions.length) this.questions = this.academy.questionsOf(lesson);
    this.status = this.academy.lessonStatus(lesson.id);
    this.next = this.academy.following(lesson.id);
    this.nextTitle = this.next ? this.academy.lessonTitle(this.next.id) : '';
    this.side = this.academy.lessonsOfTopic(lesson.topic).map((l) => ({
      lesson: l,
      title: this.academy.lessonTitle(l.id),
      status: this.academy.lessonStatus(l.id),
    }));
  }
}
